/**
 * Tiny Web-Mercator helpers for an OpenStreetMap raster map.
 *
 * Leaflet is about 40KB gzipped plus CSS and marker images. This module is
 * enough to place the existing `projectMap()` markers on OSM tiles
 * (`*.tile.openstreetmap.org`, already allowlisted in the Pages `_headers`)
 * with no API key and no extra dependency. Tile math follows the OSM slippy
 * map: https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames
 */

export const TILE_SIZE = 256;
export const MIN_ZOOM = 10;
export const MAX_ZOOM = 16;

export interface LngLat {
  lat: number;
  lng: number;
}

export interface MapTile {
  key: string;
  url: string;
  left: number;
  top: number;
}

const MERCATOR_LIMIT = 85.05112878;

function clampLat(lat: number): number {
  return Math.max(-MERCATOR_LIMIT, Math.min(MERCATOR_LIMIT, lat));
}

export function lonToWorldX(lng: number, zoom: number): number {
  const size = TILE_SIZE * 2 ** zoom;
  return ((lng + 180) / 360) * size;
}

export function latToWorldY(lat: number, zoom: number): number {
  const size = TILE_SIZE * 2 ** zoom;
  const rad = (clampLat(lat) * Math.PI) / 180;
  const merc = Math.log(Math.tan(Math.PI / 4 + rad / 2));
  return ((1 - merc / Math.PI) / 2) * size;
}

export function worldXToLon(x: number, zoom: number): number {
  const size = TILE_SIZE * 2 ** zoom;
  return (x / size) * 360 - 180;
}

export function worldYToLat(y: number, zoom: number): number {
  const size = TILE_SIZE * 2 ** zoom;
  const n = Math.PI - (2 * Math.PI * y) / size;
  return (180 / Math.PI) * Math.atan(Math.sinh(n));
}

export function viewportOrigin(
  centerLat: number,
  centerLng: number,
  zoom: number,
  width: number,
  height: number,
): { left: number; top: number } {
  return {
    left: lonToWorldX(centerLng, zoom) - width / 2,
    top: latToWorldY(centerLat, zoom) - height / 2,
  };
}

/** Pixel position of a coordinate inside a viewport centered on `center`. */
export function projectPoint(
  point: LngLat,
  center: LngLat,
  zoom: number,
  width: number,
  height: number,
): { x: number; y: number } {
  const origin = viewportOrigin(center.lat, center.lng, zoom, width, height);
  return {
    x: lonToWorldX(point.lng, zoom) - origin.left,
    y: latToWorldY(point.lat, zoom) - origin.top,
  };
}

export function boundsCenter(points: readonly LngLat[]): LngLat {
  if (points.length === 0) return { lat: 47.6062, lng: -122.3321 };
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }
  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
}

/**
 * Largest zoom that fits every point inside the viewport with padding.
 * A tight cluster (the metro-centroid fallback piles events on one point)
 * uses a neighborhood zoom instead of the world.
 */
export function fitZoom(
  points: readonly LngLat[],
  width: number,
  height: number,
  padding = 48,
): number {
  if (points.length === 0 || width <= 0 || height <= 0) return 12;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }
  if (maxLat - minLat < 0.02 && maxLng - minLng < 0.02) return 13;
  const availW = Math.max(32, width - padding * 2);
  const availH = Math.max(32, height - padding * 2);
  for (let z = MAX_ZOOM; z >= MIN_ZOOM; z--) {
    const dx = Math.abs(lonToWorldX(maxLng, z) - lonToWorldX(minLng, z));
    const dy = Math.abs(latToWorldY(maxLat, z) - latToWorldY(minLat, z));
    if (dx <= availW && dy <= availH) return z;
  }
  return MIN_ZOOM;
}

/** OSM tiles covering the viewport. `left`/`top` are pixels relative to the viewport. */
export function tilesForViewport(
  center: LngLat,
  zoom: number,
  width: number,
  height: number,
): MapTile[] {
  const origin = viewportOrigin(center.lat, center.lng, zoom, width, height);
  const x0 = Math.floor(origin.left / TILE_SIZE);
  const y0 = Math.floor(origin.top / TILE_SIZE);
  const x1 = Math.floor((origin.left + width - 1) / TILE_SIZE);
  const y1 = Math.floor((origin.top + height - 1) / TILE_SIZE);
  const n = 2 ** zoom;
  const tiles: MapTile[] = [];
  for (let x = x0; x <= x1; x++) {
    const wrappedX = ((x % n) + n) % n;
    for (let y = y0; y <= y1; y++) {
      if (y < 0 || y >= n) continue;
      const sub = ['a', 'b', 'c'][(wrappedX + y) % 3] ?? 'a';
      tiles.push({
        key: `${zoom}/${x}/${y}`,
        url: `https://${sub}.tile.openstreetmap.org/${zoom}/${wrappedX}/${y}.png`,
        left: x * TILE_SIZE - origin.left,
        top: y * TILE_SIZE - origin.top,
      });
    }
  }
  return tiles;
}

/** Spread markers that share a coordinate so a geocode pile stays clickable. */
export function clusterOffset(
  count: number,
  index: number,
  radius = 22,
): { dx: number; dy: number } {
  if (count <= 1) return { dx: 0, dy: 0 };
  const angle = -Math.PI / 2 + (index * 2 * Math.PI) / count;
  return { dx: Math.cos(angle) * radius, dy: Math.sin(angle) * radius };
}
